import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useRoomContext, useParticipants } from '@livekit/components-react';
import { useCopilotMessages, COPILOT_TOPIC } from '../hooks/useCopilotMessages';
import CopilotSidebar from './CopilotSidebar';
import { dismissSage } from '../services/copilotService';
import {
  getParticipantDisplayName,
  getParticipantRole,
  isAgentParticipant,
} from '../utils/participantMeta';

/**
 * Host-only Sage panel. Intro → context picker (optional) → live suggestions.
 */
export default function CopilotHostPanel({
  isHost,
  isOpen,
  onClose,
  roomName,
  sessionId,
  onDismissed,
  onSagePresent,
  docked = false,
  sageInvited = false,
  sageBusy = false,
  onInviteSage,
  candidateName: candidateNameProp,
  contextPickerOpen = false,
  contextPickerProps = null,
}) {
  const room = useRoomContext();
  const participants = useParticipants();
  const [sessionStarted, setSessionStarted] = useState(false);
  /** After dismiss, ignore lingering Sage until they leave (allows re-invite). */
  const [dismissLocked, setDismissLocked] = useState(false);

  const enabled = Boolean(isHost);
  const {
    suggestions,
    ready,
    sessionComplete,
    latestNotes,
    redFlags,
    clearSuggestions,
    resetSession,
    hasPriorContext,
    contextCandidateName,
  } = useCopilotMessages({ enabled });

  const sagePresent = useMemo(
    () => (participants || []).some((p) => isAgentParticipant(p)),
    [participants]
  );

  useEffect(() => {
    if (dismissLocked && !sagePresent) {
      setDismissLocked(false);
    }
  }, [dismissLocked, sagePresent]);

  const candidateName = useMemo(() => {
    if (candidateNameProp) return candidateNameProp;
    const candidate = (participants || []).find(
      (p) => !isAgentParticipant(p) && getParticipantRole(p) === 'candidate'
    );
    return candidate ? getParticipantDisplayName(candidate) : '';
  }, [participants, candidateNameProp]);

  const effectiveSagePresent = sagePresent && !dismissLocked;
  const live = Boolean(
    sessionStarted ||
      sageInvited ||
      effectiveSagePresent ||
      (ready && !dismissLocked) ||
      sageBusy ||
      contextPickerOpen
  );

  let mode = 'intro';
  if (contextPickerOpen) mode = 'context';
  else if (live) mode = 'active';

  useEffect(() => {
    if (sageInvited || effectiveSagePresent || (ready && !dismissLocked) || contextPickerOpen) {
      setSessionStarted(true);
    }
  }, [sageInvited, effectiveSagePresent, ready, dismissLocked, contextPickerOpen]);

  useEffect(() => {
    if (
      !sageInvited &&
      !effectiveSagePresent &&
      !(ready && !dismissLocked) &&
      !sageBusy &&
      !contextPickerOpen
    ) {
      setSessionStarted(false);
    }
  }, [
    sageInvited,
    effectiveSagePresent,
    ready,
    dismissLocked,
    sageBusy,
    contextPickerOpen,
  ]);

  useEffect(() => {
    if (!isHost || !onSagePresent) return undefined;
    if (dismissLocked) return undefined;
    if (ready) {
      onSagePresent({ sessionId: sessionId || '' });
      return undefined;
    }
    const sage = (participants || []).find((p) => isAgentParticipant(p));
    if (sage) {
      let sid = '';
      try {
        const meta = sage.metadata ? JSON.parse(sage.metadata) : {};
        sid = meta?.session_id || '';
      } catch {
        /* ignore */
      }
      onSagePresent({ sessionId: sid });
    }
    return undefined;
  }, [isHost, ready, participants, onSagePresent, sessionId, dismissLocked]);

  const handleAllowJoin = useCallback(
    async (language) => {
      setDismissLocked(false);
      setSessionStarted(true);
      if (!onInviteSage) return;
      try {
        await onInviteSage(language);
      } catch {
        if (!sageInvited && !sagePresent && !contextPickerOpen) {
          setSessionStarted(false);
        }
      }
    },
    [onInviteSage, sageInvited, sagePresent, contextPickerOpen]
  );

  const handleDismiss = async () => {
    setDismissLocked(true);
    setSessionStarted(false);
    resetSession();
    try {
      if (room?.localParticipant) {
        const payload = new TextEncoder().encode(
          JSON.stringify({ type: 'dismiss_sage', session_id: sessionId || '' })
        );
        await room.localParticipant.publishData(payload, {
          reliable: true,
          topic: COPILOT_TOPIC,
        });
      }
    } catch (e) {
      console.error('Failed to publish dismiss_sage:', e);
    }
    try {
      if (roomName) {
        await dismissSage({ roomName, session_id: sessionId });
      }
    } catch (e) {
      console.error('Failed to call dismissSage API:', e);
    }
    clearSuggestions();
    if (onDismissed) onDismissed();
  };

  if (!isHost) {
    return null;
  }

  return (
    <CopilotSidebar
      isOpen={isOpen}
      onClose={onClose}
      suggestions={suggestions}
      ready={ready}
      sessionComplete={sessionComplete}
      latestNotes={latestNotes}
      redFlags={redFlags}
      onClear={clearSuggestions}
      onDismiss={mode === 'active' ? handleDismiss : undefined}
      docked={docked}
      mode={mode}
      onAllowJoin={handleAllowJoin}
      inviting={sageBusy}
      candidateName={candidateName || contextCandidateName}
      resumeMatch={hasPriorContext ? 'Loaded' : undefined}
      assessmentLabel={hasPriorContext ? 'Loaded' : undefined}
      contextPicker={mode === 'context' ? contextPickerProps : null}
    />
  );
}
